import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const CompetitorSharingModal = ({ competitor, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [shareEmail, setShareEmail] = useState('');
  const [shareUserId, setShareUserId] = useState('');
  const [accessLevel, setAccessLevel] = useState('viewer');
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [loadingAccessList, setLoadingAccessList] = useState(false);
  const [accessListError, setAccessListError] = useState(null);

  useEffect(() => {
    if (competitor) {
      loadAccessList();
    }
  }, [competitor]);

  const loadAccessList = async () => {
    if (!competitor || !user) return;

    try {
      setLoadingAccessList(true);
      setAccessListError(null);
      
      const response = await ApiService.getCompetitorAccessList(
        competitor.competitor_id, 
        user.user_id
      );
      
      if (response.success) {
        setAccessList(response.access_list || []);
      } else {
        setAccessListError(response.error || 'Failed to load access list');
      }
    } catch (err) {
      // Error loading access list
      setAccessListError('Failed to load access list');
    } finally {
      setLoadingAccessList(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!shareEmail && !shareUserId) {
      setShareError('Please provide either an email or user ID');
      return;
    }

    if (!competitor || !user) {
      setShareError('Missing competitor or user information');
      return;
    }

    try {
      setIsSharing(true);
      setShareError(null);
      
      const response = await ApiService.shareCompetitor({
        competitor_id: competitor.competitor_id,
        target_email: shareEmail || undefined,
        target_user_id: shareUserId || undefined,
        access_level: accessLevel,
        granted_by_user_id: user.user_id
      });
      
      if (response.success) {
        setShareEmail('');
        setShareUserId('');
        setAccessLevel('viewer');
        await loadAccessList(); // Refresh the access list
        
        if (onSuccess) {
          onSuccess(response);
        }
      } else {
        setShareError(response.error || 'Failed to share competitor');
      }
    } catch (err) {
      // Error sharing competitor
      setShareError('Failed to share competitor');
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevoke = async (userId) => {
    if (!competitor || !user) return;

    try {
      const response = await ApiService.revokeCompetitorAccess({
        competitor_id: competitor.competitor_id,
        target_user_id: userId,
        revoked_by_user_id: user.user_id
      });
      
      if (response.success) {
        await loadAccessList(); // Refresh the access list
        if (onSuccess) {
          onSuccess(response);
        }
      } else {
        setAccessListError(response.error || 'Failed to revoke access');
      }
    } catch (err) {
      // Error revoking access
      setAccessListError('Failed to revoke access');
    }
  };

  if (!competitor) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share Competitor: {competitor.name}</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="modal-body">
          {/* Share Form */}
          <div className="share-section">
            <h3>Grant Access</h3>
            <form onSubmit={handleShare}>
              <div className="form-group">
                <label htmlFor="shareEmail">User Email:</label>
                <input
                  id="shareEmail"
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={isSharing || !!shareUserId}
                />
              </div>

              <div className="form-group">
                <label>OR</label>
              </div>

              <div className="form-group">
                <label htmlFor="shareUserId">User ID:</label>
                <input
                  id="shareUserId"
                  type="text"
                  value={shareUserId}
                  onChange={(e) => setShareUserId(e.target.value)}
                  placeholder="user_12345"
                  disabled={isSharing || !!shareEmail}
                />
              </div>

              <div className="form-group">
                <label htmlFor="accessLevel">Access Level:</label>
                <select
                  id="accessLevel"
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  disabled={isSharing}
                >
                  <option value="viewer">Viewer (can see progress)</option>
                  <option value="manager">Manager (can share with others)</option>
                </select>
              </div>

              {shareError && (
                <div className="error-message">
                  {shareError}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={isSharing || (!shareEmail && !shareUserId)}
                >
                  {isSharing ? 'Sharing...' : 'Grant Access'}
                </button>
              </div>
            </form>
          </div>

          {/* Access List */}
          <div className="access-list-section">
            <h3>Current Access</h3>
            
            {loadingAccessList ? (
              <div className="loading">Loading access list...</div>
            ) : accessListError ? (
              <div className="error-message">
                {accessListError}
                <button onClick={loadAccessList} className="retry-button">
                  Retry
                </button>
              </div>
            ) : (
              <div className="access-list">
                {accessList.length === 0 ? (
                  <p>No users have been granted access to this competitor.</p>
                ) : (
                  <div className="access-items">
                    {accessList.map((access) => (
                      <div key={access.user_id} className="access-item">
                        <div className="access-info">
                          <div className="access-user">
                            <strong>{access.name || 'Unknown User'}</strong>
                            <span className="access-email">{access.email}</span>
                          </div>
                          <div className="access-details">
                            <span className={`access-level ${access.access_level}`}>
                              {access.access_level}
                            </span>
                            <span className="access-date">
                              Granted: {new Date(access.granted_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevoke(access.user_id)}
                          className="revoke-button"
                          title="Revoke access"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-content {
            background: white;
            border-radius: 8px;
            width: 90%;
            max-width: 600px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #e9ecef;
          }

          .modal-header h2 {
            margin: 0;
            color: #333;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
          }

          .close-button:hover {
            color: #333;
          }

          .modal-body {
            padding: 20px;
          }

          .share-section {
            margin-bottom: 30px;
          }

          .share-section h3 {
            margin: 0 0 15px 0;
            color: #333;
          }

          .form-group {
            margin-bottom: 15px;
          }

          .form-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
            color: #333;
          }

          .form-group input,
          .form-group select {
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 1rem;
          }

          .form-group input:focus,
          .form-group select:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0,123,255,.25);
          }

          .form-group input:disabled,
          .form-group select:disabled {
            background-color: #f8f9fa;
            color: #6c757d;
          }

          .error-message {
            background: #f8d7da;
            color: #721c24;
            padding: 10px;
            border-radius: 4px;
            margin-bottom: 15px;
            font-size: 0.9rem;
          }

          .form-actions {
            display: flex;
            gap: 10px;
          }

          .primary-button {
            background-color: #007bff;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 1rem;
            transition: background-color 0.2s;
          }

          .primary-button:hover:not(:disabled) {
            background-color: #0056b3;
          }

          .primary-button:disabled {
            background-color: #6c757d;
            cursor: not-allowed;
          }

          .access-list-section h3 {
            margin: 0 0 15px 0;
            color: #333;
            padding-top: 20px;
            border-top: 1px solid #e9ecef;
          }

          .loading {
            text-align: center;
            padding: 20px;
            color: #666;
          }

          .retry-button {
            background: #dc3545;
            color: white;
            border: none;
            padding: 4px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.85rem;
            margin-left: 10px;
          }

          .access-items {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .access-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 15px;
          }

          .access-info {
            flex: 1;
          }

          .access-user strong {
            display: block;
            color: #333;
            margin-bottom: 2px;
          }

          .access-email {
            color: #666;
            font-size: 0.9rem;
          }

          .access-details {
            margin-top: 5px;
            display: flex;
            gap: 15px;
            align-items: center;
          }

          .access-level {
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 0.8rem;
            font-weight: 500;
            text-transform: capitalize;
          }

          .access-level.owner {
            background: #d4edda;
            color: #155724;
          }

          .access-level.manager {
            background: #d1ecf1;
            color: #0c5460;
          }

          .access-level.viewer {
            background: #f8d7da;
            color: #721c24;
          }

          .access-date {
            color: #666;
            font-size: 0.8rem;
          }

          .revoke-button {
            background: #dc3545;
            color: white;
            border: none;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .revoke-button:hover {
            background: #c82333;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CompetitorSharingModal;